class ShippingMethods extends HTMLElement {
  static steps = ["method", "address", "date-time"];

  connectedCallback() {
    this.cartForm = document.getElementById("cart");

    if (!this.cartForm) {
      throw new Error(
        'ShippingMethods: No form with id="cart" found on the page.',
      );
    }

    this.init();

    // Handle page show due to bfcache
    const pageShowHandler = this.handlePageShow.bind(this);
    window.addEventListener("pageshow", pageShowHandler);

    // Temporary override go to /cart
    const viewCartBtn = this.parentElement.querySelector("#ViewCart");
    if (viewCartBtn) {
      viewCartBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // All-step listeners
    this.querySelectorAll("[data-close]").forEach((btn) =>
      btn.addEventListener("click", this.close.bind(this)),
    );
    this.querySelectorAll("[data-back]").forEach((btn) =>
      btn.addEventListener("click", this.handlePrevStepClick.bind(this)),
    );
    this.querySelectorAll("[data-next]").forEach((btn) =>
      btn.addEventListener("click", this.handleNextStepClick.bind(this)),
    );

    // Main: checkout listener
    this.querySelector("[data-checkout]")?.addEventListener(
      "click",
      this.handleCheckoutClick.bind(this),
    );

    // Express checkout listener
    this.querySelector("[data-express-checkout]")?.addEventListener(
      "click",
      this.handleExpressCheckoutClick.bind(this),
    );

    // Address step listeners
    this.querySelector('[name="shipping-postal"]')?.addEventListener(
      "input",
      this.handlePostalInputChange.bind(this),
    );
    this.querySelector("[data-clear-zip]")?.addEventListener(
      "click",
      this.handlePostalInputClear.bind(this),
    );
    this.querySelector("[data-locate]")?.addEventListener(
      "click",
      this.handleLocatePostalCode.bind(this),
    );
    this.querySelector("address-autocomplete")?.addEventListener(
      "address-selected",
      this.handleAutocompleteAddressSelected.bind(this),
    );
    this.querySelector("address-autocomplete")?.addEventListener(
      "address-clear",
      this.handleAutocompleteAddressClear.bind(this),
    );
    this.querySelector("address-autocomplete")?.addEventListener(
      "autocomplete-error",
      this.handleAutocompleteError.bind(this),
    );

    // Date / time step listeners
    this.addEventListener("date-selected", this.handleDateSelected.bind(this));
    this.addEventListener("time-selected", this.handleTimeSelected.bind(this));

    // Zip chip listeners
    this.querySelector("[data-zip-chip-accept]")?.addEventListener(
      "click",
      this.handleZipChipAccept.bind(this),
    );
    this.querySelector("[data-zip-chip-dismiss]")?.addEventListener(
      "click",
      this.handleZipChipDismiss.bind(this),
    );
    this.querySelector('[name="shipping-postal"]')?.addEventListener(
      "click",
      this.handlePostalInputClick.bind(this),
    );
  }

  disconnectedCallback() {
    window.removeEventListener("pageshow", this.pageShowHandler);
  }

  init() {
    const allMethods = ["localDelivery", "storePickup"];

    const availableMethods = this.getZoneAvailableMethods();

    if (availableMethods.length === 0) {
      const err = this.getI18n().noMethodsAvailable;
      alert(err);
      throw new Error("ShippingMethods: No shipping methods are available.");
    }

    this.backupFormActionAttr();

    this.initPickupAddressText();
    this.initDiscountBadge();

    const unavailableMethods = allMethods.filter(
      (m) => !availableMethods.includes(m),
    );

    this.hideMethods(unavailableMethods);

    let selectedMethod = this.getSelectedRadioMethod();

    // Handle selected method no longer available
    if (unavailableMethods.includes(selectedMethod)) {
      selectedMethod = availableMethods[0];
      this.selectRadioMethod(selectedMethod);
      this.clearShippingAddress();
      this.setPostalInputRequired(selectedMethod);
      if (selectedMethod === "storePickup") {
        this.setPostalInputValue("");
      }
      this.setCartAttr("Delivery-Date", "");
      this.setCartAttr("Delivery-Time", "");
    }

    this.setCartAttr("Delivery-Method", selectedMethod);
  }

  getSelectedRadioMethod() {
    const selected = this.querySelector('[name="shipping-method"]:checked');
    return selected ? selected.value : "";
  }

  selectRadioMethod(availableMethod) {
    const radios = this.querySelectorAll('[name="shipping-method"]');
    const radio = [...radios].find((radio) => radio.value === availableMethod);
    radio.checked = true;
  }

  handleAutocompleteAddressSelected(e) {
    const parsed = this.parseAddressComponents(e.detail.addressComponents);

    this.setShippingField("address1", parsed.address1);
    this.setShippingField("city", parsed.city);
    this.setShippingField("province", parsed.province);
    this.setShippingField("country", parsed.country);
    this.setShippingField("zip", parsed.zip);

    this.setPostalInputValue(parsed.zip);
    this.syncPostalCode(parsed.zip);

    const { latitude, longitude } = e.detail.geometry || {};
    this.syncExpressState({ latitude, longitude });
  }

  handleAutocompleteAddressClear() {
    this.clearShippingAddress();
    this.syncExpressState({ latitude: null, longitude: null, zip: null });
  }

  handleAutocompleteError() {
    const addressField = this.querySelector("[data-address-field]");
    addressField.hidden = true;
    const orDivider = this.querySelector(".shipping-methods__or");
    orDivider.hidden = true;
  }

  parseAddressComponents(components) {
    const get = (type) => components.find((c) => c.types.includes(type));

    const streetNumber = get("street_number")?.longText || "";
    const route = get("route")?.longText || "";

    return {
      address1: [streetNumber, route].filter(Boolean).join(" "),
      city:
        (
          get("locality") ||
          get("administrative_area_level_3") ||
          get("administrative_area_level_2")
        )?.longText || "",
      zip: get("postal_code")?.longText || "",
      province: get("administrative_area_level_1")?.longText || "",
      country: get("country")?.shortText || "",
    };
  }

  handlePostalInputChange(e) {
    const zip = e.target.value.replace(/\s+/g, "");
    if (e.target.value !== zip) {
      e.target.value = zip;
    }
    this.syncPostalCode(zip);
    this.syncExpressState({ zip });
  }

  setPostalInputValue(value) {
    const postalInput = this.querySelector('[name="shipping-postal"]');
    postalInput.value = (value || "").replace(/\s+/g, "").toUpperCase();
  }

  handlePostalInputClear(e) {
    this.setPostalInputValue("");
    this.syncPostalCode("");
  }

  getPostalInputValue() {
    const postalInput = this.querySelector('[name="shipping-postal"]');
    return postalInput ? postalInput.value : "";
  }

  setPostalInputRequired(method) {
    const isPickup = method === "storePickup";
    const postal = this.querySelector('[name="shipping-postal"]');
    postal.required = !isPickup;
  }

  syncPostalCode(v) {
    this.setCartAttr("Delivery-ZIP", v);
    this.setShippingField("zip", v);
    if (!v) {
      this.clearShippingAddress();
    } else {
      this.hideZipChip();
    }
    const clearBtn = this.querySelector("[data-clear-zip]");
    clearBtn.hidden = !v;
  }

  handleLocatePostalCode() {
    const btn = this.querySelector("[data-locate]");
    const postalInput = this.querySelector('[name="shipping-postal"]');
    const i18n = this.getI18n();

    if (!navigator.geolocation) {
      this.showError(i18n.locateErrorUnsupported || "", "address");
      return;
    }

    this.clearErrors();
    btn.setAttribute("aria-busy", "true");
    btn.textContent = i18n.locating || "";

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            {
              headers: {
                "Accept-Language": document.documentElement.lang || "el",
              },
            },
          );
          const data = await response.json();
          const postcode = data?.address?.postcode;
          if (postcode) {
            this.setPostalInputValue(postcode);
            this.syncPostalCode(postcode);
            this.syncExpressState({ zip: this.getPostalInputValue() });
          } else {
            this.showError(i18n.locateErrorNotFound || "", "address");
          }
        } catch {
          this.showError(i18n.locateErrorFetch || "", "address");
        } finally {
          btn.removeAttribute("aria-busy");
          btn.textContent = i18n.locateZip || "";
        }
      },
      (err) => {
        btn.removeAttribute("aria-busy");
        btn.textContent = i18n.locateZip || "";
        if (err.code === err.PERMISSION_DENIED) {
          this.showError(i18n.locateErrorDenied || "", "address");
        } else {
          this.showError(i18n.locateErrorFetch || "", "address");
        }
      },
      { timeout: 10000 },
    );
  }

  setShippingField(field, value) {
    const input = this.querySelector(`[data-shipping-field="${field}"]`);
    if (input) {
      input.value = value || "";
    }
  }

  clearShippingAddress() {
    ["address1", "city", "province", "country"].forEach((field) => {
      this.setShippingField(field, "");
    });
  }

  persistZip() {
    const zip = this.getPostalInputValue();
    if (!zip) {
      return;
    }
    const normalized = this.normalizeZip(zip);
    this.saveZip(normalized);
    const dismissed = this.getDismissedZips();
    if (dismissed.has(normalized)) {
      dismissed.delete(normalized);
      this.saveDismissedZips(dismissed);
    }
  }

  submitCartWithAddress() {
    const originalAction =
      this.cartForm.dataset.originalAction || this.cartForm.action;
    const url = new URL(originalAction, window.location.origin);
    ["address1", "city", "province", "country", "zip"].forEach((field) => {
      const val = this.querySelector(`[data-shipping-field="${field}"]`)?.value;
      if (val) {
        url.searchParams.set(`checkout[shipping_address][${field}]`, val);
      }
    });
    this.cartForm.action = url.toString();
    this.cartForm.submit();
  }

  handleCheckoutClick() {
    const selectedDate = this.getCartAttr("Delivery-Date");
    const checkedTime = this.querySelector('[name="delivery_time"]:checked');
    if (!selectedDate) {
      this.showError(this.getI18n().checkoutErrorNoDate || "", "calendar");
      return;
    }
    if (!checkedTime) {
      this.showError(this.getI18n().checkoutErrorNoTime || "", "calendar");
      return;
    }
    this.clearErrors();

    // TODO: re-set Delivery-Method and Delivery-ZIP from the form here. Express checkout
    // overwrites them, and a bfcache Back from checkout can skip the method step, so a
    // normal order gets submitted as Delivery-Method=express with an empty ZIP (E-23)

    this.persistZip();

    this.submitCartWithAddress();
  }

  handleExpressCheckoutClick() {
    this.clearErrors();

    // The zone can go out of hours while the drawer is open
    if (!this.isExpressAvailableNow()) {
      this.syncExpressState({ zip: this.getPostalInputValue() });
      return;
    }

    this.setCartAttr("Delivery-ZIP", "");
    this.setCartAttr("Delivery-Date", "");
    this.setCartAttr("Delivery-Time", "");
    this.setCartAttr("Delivery-Location", "");
    this.setCartAttr("Delivery-Method", "express");

    this.persistZip();

    this.submitCartWithAddress();
  }

  handleDateSelected(e) {
    const date = e.detail.date;
    this.setCartAttr("Delivery-Date", this.isoDate(date));
    this.setCartAttr("Delivery-Time", "");
  }

  handleTimeSelected(e) {
    this.setCartAttr("Delivery-Time", e.detail.time);
  }

  isoDate(date) {
    const yyyy = date.getFullYear();
    const mm = (date.getMonth() + 1).toString().padStart(2, "0");
    const dd = date.getDate().toString().padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  open() {
    this.classList.add("active");
    this.goToStep(ShippingMethods.steps[0]);
  }

  close() {
    this.classList.remove("active");
    this.dataset.currStep = "none";
  }

  goToStep(step) {
    this.dataset.currStep = step;
  }

  handlePrevStepClick() {
    const current = this.dataset.currStep;
    const index = ShippingMethods.steps.indexOf(current);

    if (index <= 0) {
      return;
    }

    if (current === "date-time") {
      const method = this.getSelectedRadioMethod();
      if (method === "storePickup") {
        this.goToStep("method");
        return;
      }
    }

    this.goToStep(ShippingMethods.steps[index - 1]);
  }

  handleNextStepClick() {
    const current = this.dataset.currStep;
    const index = ShippingMethods.steps.indexOf(current);

    if (index === -1 || index === ShippingMethods.steps.length - 1) {
      return;
    }

    if (current === "method") {
      const method = this.getSelectedRadioMethod();
      this.setPostalInputRequired(method);
      this.setCartAttr("Delivery-Method", method);
      this.setCartAttr("Delivery-Date", "");
      this.setCartAttr("Delivery-Time", "");

      if (method === "storePickup") {
        this.setPostalInputValue("");
        this.applyZoneConfig();
        this.goToStep("date-time");
        return;
      } else {
        this.maybeShowZipChip();

        // The ZIP can be prefilled from cart attributes; keep a match from an earlier visit
        const banner = this.querySelector("[data-express-banner]");
        if (banner.dataset.state === "collapsed") {
          this.syncExpressState({ zip: this.getPostalInputValue() });
        }
      }
    }

    if (current === "address") {
      const postalInput = this.querySelector('[name="shipping-postal"]');
      if (!postalInput.reportValidity()) {
        return;
      }
      const zip = this.getPostalInputValue();
      if (zip && !this.isZipSupported(zip)) {
        const err = this.getI18n().zipNotSupported;
        this.showError(err.replace("{zip}", zip), "address");
        return;
      }

      // Not syncing here because the input
      // event should have already done it
    }

    if (ShippingMethods.steps[index + 1] === "date-time") {
      this.applyZoneConfig();
    }

    this.goToStep(ShippingMethods.steps[index + 1]);
  }

  getZones() {
    if (this._zones) {
      return this._zones;
    }
    const script = this.querySelector("script[data-shipping-config]");
    if (!script) {
      return [];
    }
    try {
      const parsed = JSON.parse(script.textContent);
      this._zones = (parsed.zones || []).filter(function isEnabled(z) {
        return z.enabled;
      });
      return this._zones;
    } catch (e) {
      return [];
    }
  }

  resolveZone(method) {
    const zones = this.getZones();
    if (method === "storePickup") {
      return (
        zones.find(function isPickup(z) {
          return z.pickupEnabled;
        }) || null
      );
    }
    const zip = this.getPostalInputValue();
    const byZip = zones.find(function matchesZip(z) {
      if (!z.deliveryEnabled) {
        return false;
      }
      return (z.deliveryPostalAreas || []).some(function inArea(area) {
        return (area.postalCodes || []).includes(zip);
      });
    });
    if (byZip) {
      return byZip;
    }
    return (
      zones.find(function isDelivery(z) {
        return z.deliveryEnabled;
      }) || null
    );
  }

  computeSlots(zone) {
    const slots = {};
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const blackout = new Set(zone.blackoutDates || []);
    const maxDays = zone.maxFutureDays || 14;
    const calendar = this.getCalendar();

    for (let i = 0; i <= maxDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const key = this.isoDate(date);
      if (blackout.has(key)) {
        continue;
      }

      const dayKey = dayNames[date.getDay()];
      const dayConfig = zone.schedule && zone.schedule[dayKey];

      if (!dayConfig) {
        continue;
      }

      if (!this.dataset.isB2b && !dayConfig.enabled) {
        continue;
      }

      if (this.dataset.isB2b && !dayConfig.enabledB2B) {
        continue;
      }

      let earliestSlotMin = -Infinity;
      if (i === 0) {
        const nowMin = now.getHours() * 60 + now.getMinutes();
        if (nowMin >= this.timeToMinutes(dayConfig.cutoffTime)) {
          continue;
        }
        const prepH =
          typeof dayConfig.preparationHours === "number"
            ? dayConfig.preparationHours
            : 0;
        earliestSlotMin = nowMin + prepH * 60;
      }

      const calDay = calendar[key] || {};
      const daySlots = dayConfig.timeSlots || [];
      const startsInTime = (ts) =>
        this.timeToMinutes(ts.from) >= earliestSlotMin;

      if (daySlots.length && !daySlots.some(startsInTime)) {
        continue;
      }

      slots[key] = daySlots
        .filter(startsInTime)
        .filter(function notFullyBooked(ts) {
          const limit = ts.orderLimit;
          if (limit === null || limit === undefined) {
            return true;
          }
          const slotKey = `${ts.from}-${ts.to}`;
          const slotBookings = calDay[slotKey];
          if (!slotBookings) {
            return true;
          }
          const remainingForZone = slotBookings[zone.name];
          if (typeof remainingForZone === "number") {
            return remainingForZone > 0;
          }
          return true;
        })
        .map(function toRange(ts) {
          return `${ts.from}-${ts.to}`;
        });
    }

    return slots;
  }

  maybeShowZipChip() {
    if (this.getPostalInputValue()) {
      return;
    }
    const dismissed = this.getDismissedZips();
    const candidates = [
      this.getSavedZip(),
      this.dataset.customerZip || "",
    ].filter(Boolean);
    const zip = candidates.find((z) => !dismissed.has(this.normalizeZip(z)));
    if (!zip) {
      return;
    }
    this.showZipChip(this.normalizeZip(zip));
  }

  showZipChip(zip) {
    const chip = this.querySelector("[data-zip-chip]");
    if (!chip) {
      return;
    }
    const valueEl = chip.querySelector("[data-zip-chip-value]");
    if (valueEl) {
      valueEl.textContent = zip;
    }
    chip.hidden = false;
  }

  hideZipChip() {
    const chip = this.querySelector("[data-zip-chip]");
    if (chip) {
      chip.hidden = true;
    }
  }

  handleZipChipAccept() {
    const chip = this.querySelector("[data-zip-chip]");
    const zip = chip?.querySelector("[data-zip-chip-value]")?.textContent || "";
    if (zip) {
      this.setPostalInputValue(zip);
      this.syncPostalCode(zip);
      this.syncExpressState({ zip: this.getPostalInputValue() });
    }
    this.hideZipChip();
  }

  handlePostalInputClick() {
    const chip = this.querySelector("[data-zip-chip]");
    if (chip && !chip.hidden) {
      this.handleZipChipDismiss();
    }
  }

  handleZipChipDismiss() {
    const chip = this.querySelector("[data-zip-chip]");
    const zip = chip?.querySelector("[data-zip-chip-value]")?.textContent || "";
    if (zip) {
      const dismissed = this.getDismissedZips();
      dismissed.add(this.normalizeZip(zip));
      this.saveDismissedZips(dismissed);
    }
    this.hideZipChip();
  }

  getSavedZip() {
    try {
      return localStorage.getItem("shipping-zip") || "";
    } catch {
      return "";
    }
  }

  saveZip(zip) {
    try {
      localStorage.setItem("shipping-zip", zip);
    } catch {
      // ignore – storage may be unavailable
    }
  }

  normalizeZip(zip) {
    return (zip || "").replace(/\s+/g, "").toUpperCase();
  }

  getDismissedZips() {
    try {
      return new Set(
        JSON.parse(localStorage.getItem("shipping-zip-dismissed")) || [],
      );
    } catch {
      return new Set();
    }
  }

  saveDismissedZips(dismissed) {
    try {
      localStorage.setItem(
        "shipping-zip-dismissed",
        JSON.stringify([...dismissed]),
      );
    } catch {
      // ignore – storage may be unavailable
    }
  }

  getCalendar() {
    if (this._calendar !== undefined) {
      return this._calendar;
    }
    const script = this.querySelector("script[data-calendar]");
    if (!script || !script.textContent.trim()) {
      this._calendar = {};
      return this._calendar;
    }
    try {
      this._calendar = JSON.parse(script.textContent) || {};
    } catch {
      this._calendar = {};
    }
    return this._calendar;
  }

  isZipSupported(zip) {
    const deliveryZones = this.getZones().filter((z) => z.deliveryEnabled);
    const hasRestrictions = deliveryZones.some((z) =>
      z.deliveryPostalAreas?.some((a) => a.postalCodes?.length),
    );
    if (!hasRestrictions) {
      return true;
    }
    return deliveryZones.some((z) =>
      z.deliveryPostalAreas?.some((a) => a.postalCodes?.includes(zip)),
    );
  }

  applyZoneConfig() {
    const method = this.getSelectedRadioMethod();
    const zone = this.resolveZone(method);
    if (!zone) {
      return;
    }

    this.setCartAttr("Delivery-Location", zone.name || "");

    const slots = this.computeSlots(zone);
    const datePicker = this.querySelector("date-picker");
    if (datePicker && typeof datePicker.setSlots === "function") {
      datePicker.setSlots(slots);
    }
  }

  getExpressZone() {
    const zones = this.getZones();
    return (
      zones.find(function isExpress(z) {
        return z.expressEnabled;
      }) || null
    );
  }

  getExpressZipCodes() {
    const zone = this.getExpressZone();
    if (!zone) {
      return [];
    }
    return (zone.deliveryPostalAreas || []).flatMap(function toCodes(area) {
      return area.postalCodes || [];
    });
  }

  timeToMinutes(time) {
    const [hours, minutes] = (time || "").split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return NaN;
    }
    return hours * 60 + minutes;
  }

  isExpressAvailableNow() {
    const zone = this.getExpressZone();
    if (!zone) {
      return false;
    }

    const now = new Date();

    if ((zone.blackoutDates || []).includes(this.isoDate(now))) {
      return false;
    }

    const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const dayConfig = zone.schedule && zone.schedule[dayNames[now.getDay()]];

    if (!dayConfig) {
      return false;
    }

    if (!dayConfig.enabled) {
      return false;
    }

    // Timezone unaware: `now` is the shopper's device clock, while the
    // schedule times come from the store's timezone. A shopper whose device
    // is set to another timezone gets the wrong express window.
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    return (dayConfig.timeSlots || []).some((slot) => {
      const fromMinutes = this.timeToMinutes(slot.from);
      const toMinutes = this.timeToMinutes(slot.to);
      if (Number.isNaN(fromMinutes) || Number.isNaN(toMinutes)) {
        return false;
      }
      return nowMinutes >= fromMinutes && nowMinutes < toMinutes;
    });
  }

  getIsAddressInArea(lat = 0, lng = 0, geojson = {}) {
    const polygons = [];

    const collect = (geom) => {
      if (!geom) return;
      if (geom.type === "Polygon") polygons.push(geom.coordinates);
      else if (geom.type === "MultiPolygon") polygons.push(...geom.coordinates);
      else if (geom.type === "GeometryCollection")
        geom.geometries.forEach(collect);
    };

    if (geojson.type === "FeatureCollection")
      geojson.features.forEach((f) => collect(f.geometry));
    else if (geojson.type === "Feature") collect(geojson.geometry);
    else collect(geojson);

    // ray casting on a single ring; ring coords are [lng, lat]
    const inRing = (ring) => {
      let inside = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i];
        const [xj, yj] = ring[j];
        if (
          yi > lat !== yj > lat &&
          lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
        ) {
          inside = !inside;
        }
      }
      return inside;
    };

    return polygons.some(
      ([outer, ...holes]) => inRing(outer) && !holes.some(inRing),
    );
  }

  getExpressGeojson() {
    if (this._expressGeojson !== undefined) {
      return this._expressGeojson;
    }
    const script = this.querySelector("script[data-express-geojson]");
    if (!script || !script.textContent.trim()) {
      this._expressGeojson = {};
      return this._expressGeojson;
    }
    try {
      this._expressGeojson = JSON.parse(script.textContent) || {};
    } catch {
      this._expressGeojson = {};
    }
    return this._expressGeojson;
  }

  getIsAddressInExpressGeojson(lat = 0, lng = 0) {
    return this.getIsAddressInArea(lat, lng, this.getExpressGeojson());
  }

  syncExpressState({ zip = "", latitude = null, longitude = null } = {}) {
    const banner = this.querySelector("[data-express-banner]");

    if (!this.isExpressAvailableNow()) {
      banner.dataset.state = "collapsed";
      return;
    }

    const isMatch =
      longitude !== null &&
      latitude !== null &&
      this.getIsAddressInExpressGeojson(latitude, longitude);

    if (isMatch) {
      banner.dataset.state = "match";
      return;
    } else if (
      longitude !== null &&
      latitude !== null &&
      banner.dataset.state === "possible-match"
    ) {
      banner.dataset.state = "no-match";
      return;
    }

    if (zip) {
      const zipCodes = this.getExpressZipCodes();
      banner.dataset.state = zipCodes.includes(zip)
        ? "possible-match"
        : "collapsed";
      return;
    }

    banner.dataset.state = "collapsed";
  }

  handlePageShow(e) {
    if (!e.persisted) {
      return;
    }
    this.restoreFormActionAttr();
  }

  backupFormActionAttr() {
    if (this.cartForm.dataset.originalAction) {
      return;
    }
    this.cartForm.dataset.originalAction = this.cartForm.action;
  }

  restoreFormActionAttr() {
    if (!this.cartForm.dataset.originalAction) {
      return;
    }
    this.cartForm.action = this.cartForm.dataset.originalAction;
  }

  getZoneAvailableMethods() {
    const zones = this.getZones();
    const hasDelivery = zones.some((z) => z.deliveryEnabled);
    const hasPickup = zones.some((z) => z.pickupEnabled);
    const result = [];
    if (hasDelivery) {
      result.push("localDelivery");
    }
    if (hasPickup) {
      result.push("storePickup");
    }
    return result;
  }

  hideMethods(unavailableMethods) {
    const deliveryOption = this.querySelector(
      '[data-method-option="localDelivery"]',
    );
    const pickupOption = this.querySelector(
      '[data-method-option="storePickup"]',
    );

    if (deliveryOption) {
      deliveryOption.hidden = unavailableMethods.includes("localDelivery");
    }
    if (pickupOption) {
      pickupOption.hidden = unavailableMethods.includes("storePickup");
    }
  }

  initPickupAddressText() {
    const zones = this.getZones();
    const pickupZone = zones.find((z) => z.pickupEnabled);
    const pickupAddressEl = this.querySelector("[data-pickup-address]");
    if (pickupAddressEl && pickupZone?.pickupAddress) {
      pickupAddressEl.textContent = pickupZone.pickupAddress;
      pickupAddressEl.hidden = false;
    }
  }

  initDiscountBadge() {
    const zones = this.getZones();
    const pickupZone = zones.find((z) => z.pickupEnabled);

    const discountBadgeEl = this.querySelector("[data-discount-badge]");
    const text = this.dataset.isB2b
      ? pickupZone?.discountTextB2B
      : pickupZone?.discountText;
    if (discountBadgeEl && text) {
      discountBadgeEl.textContent = text;
      discountBadgeEl.hidden = false;
    }
  }

  setCartAttr(key, value) {
    const input = this.querySelector(`[data-cart-attr="${key}"]`);
    if (input) {
      input.value = value || "";
    }
  }

  getCartAttr(key) {
    const input = this.querySelector(`[data-cart-attr="${key}"]`);
    return input ? input.value : "";
  }

  showError(message, step) {
    const selector = `[data-${step}-error]`;
    const errorEl = this.querySelector(selector);
    if (errorEl) {
      errorEl.textContent = message || "";
      errorEl.hidden = false;
    }
  }

  clearErrors() {
    const selectors = ["[data-address-error]", "[data-calendar-error]"];
    const errorEls = this.querySelectorAll(selectors.join(","));
    errorEls.forEach((errorEl) => {
      errorEl.textContent = "";
      errorEl.hidden = true;
    });
  }

  getI18n() {
    if (this._i18n) {
      return this._i18n;
    }
    const script = this.querySelector("script[data-i18n]");
    if (!script) {
      return {};
    }
    try {
      this._i18n = JSON.parse(script.textContent);
      return this._i18n;
    } catch {
      return {};
    }
  }
}

if (typeof customElements.get("shipping-methods") == "undefined") {
  customElements.define("shipping-methods", ShippingMethods);
}
